import { ChevronRight } from "lucide-react";
import * as React from "react";
import { Button } from "../atoms/button.js";
import { Card, CardContent } from "../atoms/card.js";
import { Spinner } from "../atoms/spinner.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";

export type BundleAnalysisChecklistItemState = "queued" | "in-progress" | "complete";

export interface BundleAnalysisChecklistItem {
  id: string;
  inProgress: React.ReactNode;
  complete: React.ReactNode;
  /** Plain sentence for stage-card map overlay (avoids flex layout bugs from rich React). */
  overlayInProgress?: string;
  overlayComplete?: string;
}

export interface BundleAnalysisHeadlinePhases {
  initiating: React.ReactNode;
  inProgress: React.ReactNode;
  complete: React.ReactNode;
}

const DEFAULT_HEADLINE_PHASES: BundleAnalysisHeadlinePhases = {
  initiating: "Initiating the build...",
  inProgress: "Benefits build in progress...",
  complete: "Your build is complete!",
};

function resolveHeadlinePhase(
  checklistStarted: boolean,
  activeIndex: number,
  checklistLength: number,
): keyof BundleAnalysisHeadlinePhases {
  if (activeIndex >= checklistLength) return "complete";
  if (checklistStarted) return "inProgress";
  return "initiating";
}

function BundleAnalysisProgressTrack({
  progressPercent,
  progressAriaLabel,
  headlinePhase,
}: {
  progressPercent: number;
  progressAriaLabel: string;
  headlinePhase: keyof BundleAnalysisHeadlinePhases;
}) {
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: decorative progress; aria-live announces updates.
    <div
      className="cw-v2-bundle-analysis-reveal__progress-track"
      role="progressbar"
      aria-label={progressAriaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progressPercent}
      aria-live="polite"
    >
      <div
        className={cx(
          "cw-v2-bundle-analysis-reveal__progress-fill",
          headlinePhase === "complete" && "cw-v2-bundle-analysis-reveal__progress-fill--complete",
        )}
        style={{ width: `${progressPercent}%` }}
      />
    </div>
  );
}

export type BundleAnalysisRevealLayout = "default" | "immersive" | "stage-card";

export interface BundleAnalysisStageContext {
  activeIndex: number;
  activeItemId: string | null;
  itemStates: BundleAnalysisChecklistItemState[];
  checklistStarted: boolean;
  buildComplete: boolean;
}

export interface BundleAnalysisRevealProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title" | "children"> {
  /** `immersive` — full-bleed background stage with scrim; checklist and copy on top. */
  /** `stage-card` — centered map/visual card with active checklist copy overlaid (Figma bundle build). */
  layout?: BundleAnalysisRevealLayout;
  /**
   * Background visuals for `layout="immersive"` or `layout="stage-card"`. Receives live
   * checklist progress so the stage can swap or animate per active step.
   */
  stage?: React.ReactNode | ((context: BundleAnalysisStageContext) => React.ReactNode);
  /** Hero illustration above the headline (e.g. animated product grid). */
  illustration?: React.ReactNode;
  /** Static headline override. When omitted, `headlinePhases` cycle with build progress. */
  headline?: React.ReactNode;
  headlinePhases?: Partial<BundleAnalysisHeadlinePhases>;
  description?: React.ReactNode;
  checklistItems: BundleAnalysisChecklistItem[];
  revealLabel?: React.ReactNode;
  footnote?: React.ReactNode;
  onReveal: () => void;
  /** Milliseconds per checklist step. Default 1000. */
  itemDurationMs?: number;
  /** Delay before checklist animation starts. Default 960. */
  checklistStartDelayMs?: number;
  /** Stagger between intro blocks (illustration, headline, description, card). Default 120. */
  introStaggerMs?: number;
  /** Optional mono kicker above the headline; cycles with build progress. */
  eyebrowPhases?: Partial<BundleAnalysisHeadlinePhases>;
  /** Render a progress meter + step counter tied to checklist completion. */
  showProgressMeter?: boolean;
  /**
   * After the checklist finishes, replaces the step list (and progress meter) with this
   * personalized explanation before the reveal CTA.
   */
  outcomeExplanation?: React.ReactNode;
  /** Extra beat to read `outcomeExplanation` before the CTA unlocks. Default 650 when set. */
  outcomeCtaUnlockDelayMs?: number;
}

const DEFAULT_ITEM_DURATION_MS = 1000;
const DEFAULT_CTA_UNLOCK_DELAY_MS = 300;
const DEFAULT_INTRO_STAGGER_MS = 120;
const DEFAULT_INTRO_ANIMATION_MS = 600;
const DEFAULT_CHECKLIST_START_DELAY_MS = DEFAULT_INTRO_STAGGER_MS * 3 + DEFAULT_INTRO_ANIMATION_MS;

/** Snappier than hero intro — phase headline/eyebrow swaps should feel immediate, not sluggish. */
function phaseCopyIntroDelayMs(
  phase: keyof BundleAnalysisHeadlinePhases,
  introStaggerMs: number,
  slot: "eyebrow" | "headline",
): string | undefined {
  if (phase !== "initiating") return undefined;
  const multiplier = slot === "eyebrow" ? 0.5 : 1;
  return `${introStaggerMs * multiplier}ms`;
}

function resolveStageOverlayCopy(
  checklistItems: BundleAnalysisChecklistItem[],
  itemStates: BundleAnalysisChecklistItemState[],
  buildComplete: boolean,
): React.ReactNode {
  const inProgressIndex = itemStates.findIndex((state) => state === "in-progress");
  if (inProgressIndex >= 0) {
    const item = checklistItems[inProgressIndex];
    return item?.overlayInProgress ?? item?.inProgress ?? null;
  }

  if (buildComplete) {
    const lastItem = checklistItems[checklistItems.length - 1];
    return lastItem?.overlayComplete ?? lastItem?.complete ?? null;
  }

  const firstItem = checklistItems[0];
  return firstItem?.overlayInProgress ?? firstItem?.inProgress ?? null;
}

function resolveActiveItemId(
  checklistItems: BundleAnalysisChecklistItem[],
  itemStates: BundleAnalysisChecklistItemState[],
  checklistStarted: boolean,
  activeIndex: number,
  buildComplete: boolean,
): string | null {
  const inProgressIndex = itemStates.findIndex((state) => state === "in-progress");
  if (inProgressIndex >= 0) {
    return checklistItems[inProgressIndex]?.id ?? null;
  }
  if (buildComplete) {
    return checklistItems[checklistItems.length - 1]?.id ?? null;
  }
  if (!checklistStarted) {
    return checklistItems[0]?.id ?? null;
  }
  const fallbackIndex = Math.min(activeIndex, Math.max(checklistItems.length - 1, 0));
  return checklistItems[fallbackIndex]?.id ?? null;
}

/** Matches `--cw-v2-bundle-stage-copy-out-duration` — exit before next line enters. */
const STAGE_OVERLAY_EXIT_MS = 500;

type StageOverlayMotionPhase = "in" | "out";

interface StageOverlayDisplayState {
  activeItemId: string | null;
  copy: React.ReactNode;
  motionPhase: StageOverlayMotionPhase;
  /** First line waits for the stage surface intro before blur-in-up. */
  syncWithStageIntro: boolean;
}

function BundleAnalysisStageOverlayCopy({
  activeItemId,
  copy,
  enterDelayMs = 0,
}: {
  activeItemId: string | null;
  copy: React.ReactNode;
  /** Defers the first blur-in-up until the stage card surface is visible. */
  enterDelayMs?: number;
}) {
  const [displayState, setDisplayState] = React.useState<StageOverlayDisplayState>(() => ({
    activeItemId,
    copy,
    motionPhase: "in",
    syncWithStageIntro: true,
  }));
  const displayedItemIdRef = React.useRef(activeItemId);

  React.useEffect(() => {
    if (activeItemId === displayedItemIdRef.current) {
      setDisplayState((prev) => (prev.copy === copy ? prev : { ...prev, copy }));
      return undefined;
    }

    setDisplayState((prev) => ({ ...prev, motionPhase: "out", syncWithStageIntro: false }));

    const timer = window.setTimeout(() => {
      displayedItemIdRef.current = activeItemId;
      setDisplayState({
        activeItemId,
        copy,
        motionPhase: "in",
        syncWithStageIntro: false,
      });
    }, STAGE_OVERLAY_EXIT_MS);

    return () => window.clearTimeout(timer);
  }, [activeItemId, copy]);

  const enterAnimationDelay =
    displayState.motionPhase === "in" && displayState.syncWithStageIntro ? enterDelayMs : undefined;

  return (
    <div aria-live="polite" className="cw-v2-bundle-analysis-reveal__stage-card-copy">
      <p
        key={`${displayState.activeItemId ?? "idle"}-${displayState.motionPhase}`}
        className={cx(
          "cw-v2-bundle-analysis-reveal__stage-card-copy-text",
          displayState.motionPhase === "out"
            ? "cw-v2-bundle-analysis-reveal__stage-card-copy-text--out"
            : "cw-v2-bundle-analysis-reveal__stage-card-copy-text--in",
        )}
        style={
          enterAnimationDelay == null ? undefined : { animationDelay: `${enterAnimationDelay}ms` }
        }
      >
        {displayState.copy}
      </p>
    </div>
  );
}

function BundleAnalysisChecklistCard({
  checklistItems,
  itemStates,
  animationDelayMs,
}: {
  checklistItems: BundleAnalysisChecklistItem[];
  itemStates: BundleAnalysisChecklistItemState[];
  animationDelayMs?: number;
}) {
  return (
    <Card
      className={cx(
        "cw-v2-bundle-analysis-reveal__intro-block",
        "cw-v2-bundle-analysis-reveal__checklist-card",
      )}
      style={animationDelayMs == null ? undefined : { animationDelay: `${animationDelayMs}ms` }}
    >
      <CardContent className="cw-v2-bundle-analysis-reveal__checklist-body">
        {checklistItems.map((item, index) => {
          const state = itemStates[index] ?? "queued";
          const label = state === "complete" ? item.complete : item.inProgress;

          return (
            <div
              key={item.id}
              data-state={state}
              className={cx(
                "cw-v2-bundle-analysis-reveal__checklist-row",
                state === "queued" && "cw-v2-bundle-analysis-reveal__checklist-row--queued",
                state === "in-progress" && "cw-v2-bundle-analysis-reveal__checklist-row--active",
                state === "complete" && "cw-v2-bundle-analysis-reveal__checklist-row--complete",
              )}
            >
              <ChecklistStatusIcon state={state} />
              <Typography
                variant="body"
                muted
                className="cw-v2-bundle-analysis-reveal__checklist-text"
              >
                {label}
              </Typography>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ChecklistStatusIcon({ state }: { state: BundleAnalysisChecklistItemState }) {
  if (state === "complete") {
    return (
      <span
        className="cw-v2-bundle-analysis-reveal__status cw-v2-bundle-analysis-reveal__status--complete"
        aria-hidden
      >
        <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true">
          <path
            d="M16.5 5.5 8.25 13.75 4.5 10"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.75"
          />
        </svg>
      </span>
    );
  }

  if (state === "in-progress") {
    return (
      <span className="cw-v2-bundle-analysis-reveal__status">
        <Spinner size="sm" label="Analyzing step" />
      </span>
    );
  }

  return (
    <span
      className="cw-v2-bundle-analysis-reveal__status cw-v2-bundle-analysis-reveal__status--queued"
      aria-hidden
    />
  );
}

/**
 * Animated “building your package” reveal used between roster import and
 * the benefits bundle step. Parent supplies checklist copy; illustration
 * is an optional slot for app-specific assets.
 */
export const BundleAnalysisReveal = React.forwardRef<HTMLElement, BundleAnalysisRevealProps>(
  function BundleAnalysisReveal(
    {
      checklistItems,
      className,
      description = "This is the work a broker usually handles — scanning plans, checking networks, and matching coverage to your business.",
      footnote = "Pricing on the next screen is estimated. We'll confirm final rates before launch. You can adjust your contribution, swap plans, or add extras before anything goes live.",
      headline,
      headlinePhases,
      eyebrowPhases,
      showProgressMeter = false,
      outcomeExplanation,
      outcomeCtaUnlockDelayMs,
      illustration,
      layout = "default",
      stage,
      introStaggerMs = DEFAULT_INTRO_STAGGER_MS,
      itemDurationMs = DEFAULT_ITEM_DURATION_MS,
      checklistStartDelayMs = DEFAULT_CHECKLIST_START_DELAY_MS,
      onReveal,
      revealLabel = "Reveal my benefits bundle",
      ...props
    },
    ref,
  ) {
    const [checklistStarted, setChecklistStarted] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [itemStates, setItemStates] = React.useState<BundleAnalysisChecklistItemState[]>(() =>
      checklistItems.map(() => "queued"),
    );
    const [showCtaSection, setShowCtaSection] = React.useState(false);

    React.useEffect(() => {
      setItemStates(checklistItems.map(() => "queued"));
      setActiveIndex(0);
      setChecklistStarted(false);
      setShowCtaSection(false);
    }, [checklistItems]);

    React.useEffect(() => {
      const timer = window.setTimeout(() => {
        setChecklistStarted(true);
        setItemStates((prev) => {
          const next = [...prev];
          if (next.length > 0) next[0] = "in-progress";
          return next;
        });
      }, checklistStartDelayMs);

      return () => window.clearTimeout(timer);
    }, [checklistStartDelayMs]);

    React.useEffect(() => {
      if (!checklistStarted || activeIndex >= checklistItems.length) {
        return undefined;
      }

      const timer = window.setTimeout(() => {
        setItemStates((prev) => {
          const next = [...prev];
          next[activeIndex] = "complete";
          if (activeIndex + 1 < next.length) {
            next[activeIndex + 1] = "in-progress";
          }
          return next;
        });
        setActiveIndex((prev) => prev + 1);
      }, itemDurationMs);

      return () => window.clearTimeout(timer);
    }, [activeIndex, checklistItems.length, checklistStarted, itemDurationMs]);

    React.useEffect(() => {
      if (activeIndex < checklistItems.length) {
        return undefined;
      }

      const unlockDelayMs =
        outcomeExplanation != null ? (outcomeCtaUnlockDelayMs ?? 650) : DEFAULT_CTA_UNLOCK_DELAY_MS;

      const unlockTimer = window.setTimeout(() => {
        setShowCtaSection(true);
      }, unlockDelayMs);

      return () => window.clearTimeout(unlockTimer);
    }, [activeIndex, checklistItems.length, outcomeCtaUnlockDelayMs, outcomeExplanation]);

    const buildComplete = activeIndex >= checklistItems.length;
    const showOutcome = buildComplete && outcomeExplanation != null;

    const resolvedHeadlinePhases = { ...DEFAULT_HEADLINE_PHASES, ...headlinePhases };
    const headlinePhase = resolveHeadlinePhase(
      checklistStarted,
      activeIndex,
      checklistItems.length,
    );
    const resolvedHeadline = headline ?? resolvedHeadlinePhases[headlinePhase];

    const completedCount = Math.min(activeIndex, checklistItems.length);
    const totalCount = checklistItems.length;
    const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const resolvedEyebrow = eyebrowPhases?.[headlinePhase] ?? null;
    const progressAriaLabel =
      headlinePhase === "complete"
        ? "Benefits build complete"
        : `Benefits build in progress, ${completedCount} of ${totalCount} steps complete`;

    const isImmersive = layout === "immersive";
    const isStageCard = layout === "stage-card";
    const usesStageLayout = isImmersive || isStageCard;
    const showIllustration = illustration != null && !usesStageLayout;
    const activeItemId = resolveActiveItemId(
      checklistItems,
      itemStates,
      checklistStarted,
      activeIndex,
      buildComplete,
    );
    const stageContext: BundleAnalysisStageContext = {
      activeIndex,
      activeItemId,
      itemStates,
      checklistStarted,
      buildComplete,
    };
    const resolvedStage =
      stage == null ? null : typeof stage === "function" ? stage(stageContext) : stage;
    const stageOverlayCopy = resolveStageOverlayCopy(checklistItems, itemStates, buildComplete);

    const introBlock = (
      <div className="cw-v2-bundle-analysis-reveal__intro">
        {showIllustration ? (
          <div
            className="cw-v2-bundle-analysis-reveal__intro-block"
            style={{ animationDelay: `${introStaggerMs * 0}ms` }}
          >
            {illustration}
          </div>
        ) : null}
        {resolvedEyebrow && !isStageCard ? (
          <div className="cw-v2-bundle-analysis-reveal__eyebrow-slot">
            <span
              key={headlinePhase}
              aria-live="polite"
              className={cx(
                "cw-v2-bundle-analysis-reveal__phase-copy",
                "cw-v2-bundle-analysis-reveal__eyebrow",
              )}
              style={{
                animationDelay: phaseCopyIntroDelayMs(headlinePhase, introStaggerMs, "eyebrow"),
              }}
            >
              {resolvedEyebrow}
            </span>
          </div>
        ) : null}
        <div className="cw-v2-bundle-analysis-reveal__headline-slot">
          <Typography
            key={isStageCard ? undefined : headlinePhase}
            variant="display"
            as="h1"
            aria-live="polite"
            className={cx(
              isStageCard
                ? "cw-v2-bundle-analysis-reveal__intro-block"
                : "cw-v2-bundle-analysis-reveal__phase-copy",
              "cw-v2-bundle-analysis-reveal__headline",
              isStageCard && "cw-v2-bundle-analysis-reveal__headline--stage-card",
            )}
            style={{
              animationDelay: isStageCard
                ? `${introStaggerMs * 0}ms`
                : phaseCopyIntroDelayMs(headlinePhase, introStaggerMs, "headline"),
            }}
          >
            {resolvedHeadline}
          </Typography>
        </div>
        {description && !showOutcome ? (
          <Typography
            variant="body-large"
            muted
            className={cx(
              "cw-v2-bundle-analysis-reveal__intro-block",
              "cw-v2-bundle-analysis-reveal__description",
            )}
            style={{ animationDelay: `${introStaggerMs * 2}ms` }}
          >
            {description}
          </Typography>
        ) : null}
        {showProgressMeter && !showOutcome && !isStageCard ? (
          <div
            className={cx(
              "cw-v2-bundle-analysis-reveal__intro-block",
              "cw-v2-bundle-analysis-reveal__progress",
            )}
            style={{ animationDelay: `${introStaggerMs * 2.5}ms` }}
          >
            <BundleAnalysisProgressTrack
              progressPercent={progressPercent}
              progressAriaLabel={progressAriaLabel}
              headlinePhase={headlinePhase}
            />
          </div>
        ) : null}
      </div>
    );

    const showCtaBlock = isStageCard || showCtaSection;
    const ctaDisabled = isStageCard && !buildComplete;

    const ctaSection = showCtaBlock ? (
      <div
        className={cx(
          "cw-v2-bundle-analysis-reveal__cta",
          isStageCard && "cw-v2-bundle-analysis-reveal__intro-block",
        )}
        style={isStageCard ? { animationDelay: `${introStaggerMs * 4}ms` } : undefined}
      >
        <Button
          type="button"
          variant="primary"
          size="lg"
          disabled={ctaDisabled}
          iconSuffix={<ChevronRight className="size-5" aria-hidden />}
          className="cw-v2-bundle-analysis-reveal__cta-button"
          onClick={onReveal}
        >
          {revealLabel}
        </Button>
        {footnote ? (
          <Typography
            variant="body-mono-small"
            muted
            className="cw-v2-bundle-analysis-reveal__footnote"
          >
            {footnote}
          </Typography>
        ) : null}
      </div>
    ) : null;

    if (isStageCard) {
      return (
        <main
          ref={ref}
          className={cx(
            "cw-v2-bundle-analysis-reveal",
            "cw-v2-bundle-analysis-reveal--stage-card",
            className,
          )}
          {...props}
        >
          {introBlock}
          {!showOutcome ? (
            <div className="cw-v2-bundle-analysis-reveal__stage-card">
              <div
                className="cw-v2-bundle-analysis-reveal__stage-card-surface"
                style={{ animationDelay: `${introStaggerMs * 3}ms` }}
                aria-hidden="true"
              >
                <div className="cw-v2-bundle-analysis-reveal__stage-card-accent" />
                <div className="cw-v2-bundle-analysis-reveal__stage-card-media">
                  {resolvedStage}
                </div>
                <div className="cw-v2-bundle-analysis-reveal__stage-card-overlay" />
              </div>
              <BundleAnalysisStageOverlayCopy
                activeItemId={activeItemId}
                copy={stageOverlayCopy}
                enterDelayMs={introStaggerMs * 3 + 320}
              />
            </div>
          ) : null}
          {showOutcome ? (
            <Typography
              variant="body-large"
              muted
              className={cx(
                "cw-v2-bundle-analysis-reveal__intro-block",
                "cw-v2-bundle-analysis-reveal__outcome",
              )}
              aria-live="polite"
            >
              {outcomeExplanation}
            </Typography>
          ) : null}
          {ctaSection}
        </main>
      );
    }

    const foreground = (
      <>
        {introBlock}

        {showOutcome ? (
          <Typography
            variant="body-large"
            muted
            className={cx(
              "cw-v2-bundle-analysis-reveal__intro-block",
              "cw-v2-bundle-analysis-reveal__outcome",
            )}
            aria-live="polite"
          >
            {outcomeExplanation}
          </Typography>
        ) : (
          <BundleAnalysisChecklistCard
            checklistItems={checklistItems}
            itemStates={itemStates}
            animationDelayMs={introStaggerMs * 3}
          />
        )}

        {ctaSection}
      </>
    );

    return (
      <main
        ref={ref}
        className={cx(
          "cw-v2-bundle-analysis-reveal",
          isImmersive && "cw-v2-bundle-analysis-reveal--immersive",
          className,
        )}
        {...props}
      >
        {isImmersive ? (
          <div className="cw-v2-bundle-analysis-reveal__stage" aria-hidden="true">
            {resolvedStage}
            <div className="cw-v2-bundle-analysis-reveal__stage-scrim" />
          </div>
        ) : null}
        <div className="cw-v2-bundle-analysis-reveal__foreground">{foreground}</div>
      </main>
    );
  },
);
BundleAnalysisReveal.displayName = "BundleAnalysisReveal";
